import { useCallback, useEffect, useRef, useState } from "react";
import { ExternalLink, RefreshCw, AlertCircle, Tablet, Loader2 } from "lucide-react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";

const MYROAST_URL = process.env.REACT_APP_MYROAST_URL || "";

/**
 * Embeds the hosted MyRoast tablet bundle inside the MA web UI.
 *
 * Auth hand-off: we don't share MA's HttpOnly cookies (different origin,
 * different domain). Instead we mint a short-lived JWT via
 * `/api/myroast/embed-token` and pass it to the iframe via `?embed=1&token=`.
 * The tablet bundle reads it from the URL and uses it as a Bearer header.
 *
 * Token refresh: we re-issue ~15 min before expiry and reload the iframe
 * src so the tablet picks up the new token cleanly. Browsers throttle
 * background tabs, so we also re-issue on `visibilitychange → visible`.
 *
 * `REACT_APP_MYROAST_URL` should be set to the tablet bundle's hosted URL
 * (e.g. https://myroast.pages.dev). If unset, we render a friendly
 * "not configured" panel instead of a broken iframe.
 */
export default function MyRoast() {
  const [token, setToken] = useState(null);
  const [expiresAt, setExpiresAt] = useState(0);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const iframeRef = useRef(null);

  const mint = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/myroast/embed-token");
      setToken(data.token);
      setExpiresAt(data.expires_at * 1000);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Failed to get embed token");
    } finally {
      setLoading(false);
    }
  }, []);

  // First mint on mount
  useEffect(() => {
    if (MYROAST_URL) mint();
    else setLoading(false);
  }, [mint]);

  // Auto-refresh 15 min before expiry
  useEffect(() => {
    if (!expiresAt) return undefined;
    const refreshAt = expiresAt - 15 * 60 * 1000;
    const delay = Math.max(refreshAt - Date.now(), 60_000);
    const t = setTimeout(mint, delay);
    return () => clearTimeout(t);
  }, [expiresAt, mint]);

  // Re-mint when the tab comes back to focus after >15 min away
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState !== "visible") return;
      if (!expiresAt) return;
      const remaining = expiresAt - Date.now();
      if (remaining < 15 * 60 * 1000) mint();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [expiresAt, mint]);

  const iframeSrc = MYROAST_URL && token
    ? `${MYROAST_URL.replace(/\/$/, "")}/?embed=1&token=${encodeURIComponent(token)}`
    : null;

  return (
    <div data-testid="myroast-page">
      <PageHeader
        eyebrow="Tablet integration"
        title="MyRoast"
        description="Embedded view of the MyRoast tablet — live against this workspace's data."
        testId="myroast-header"
        actions={
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={mint}
              disabled={loading || !MYROAST_URL}
              data-testid="myroast-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
              Refresh session
            </Button>
            {MYROAST_URL && (
              <Button
                variant="outline"
                size="sm"
                asChild
                data-testid="myroast-open-btn"
              >
                <a href={MYROAST_URL} target="_blank" rel="noreferrer">
                  <ExternalLink className="w-4 h-4 mr-1.5" /> Open in new tab
                </a>
              </Button>
            )}
          </>
        }
      />

      {!MYROAST_URL && <NotConfiguredPanel />}
      {MYROAST_URL && error && <ErrorPanel error={error} onRetry={mint} />}
      {MYROAST_URL && !error && loading && !token && <LoadingPanel />}
      {MYROAST_URL && !error && iframeSrc && (
        <div
          className="relative w-full rounded-lg border border-border overflow-hidden bg-card"
          style={{ height: "calc(100vh - 220px)", minHeight: 520 }}
        >
          <iframe
            ref={iframeRef}
            key={token}            /* force reload when token rotates */
            src={iframeSrc}
            title="MyRoast tablet"
            className="w-full h-full"
            data-testid="myroast-iframe"
            allow="clipboard-read; clipboard-write; bluetooth; serial"
            referrerPolicy="strict-origin-when-cross-origin"
          />
        </div>
      )}
    </div>
  );
}

function LoadingPanel() {
  return (
    <div
      className="flex flex-col items-center justify-center gap-3 rounded-lg border border-border bg-card p-12"
      data-testid="myroast-loading"
    >
      <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      <p className="text-sm text-muted-foreground">Issuing tablet session…</p>
    </div>
  );
}

function ErrorPanel({ error, onRetry }) {
  return (
    <div
      className="flex flex-col items-start gap-3 rounded-lg border border-destructive/40 bg-destructive/5 p-6"
      data-testid="myroast-error"
    >
      <div className="flex items-center gap-2 text-destructive">
        <AlertCircle className="w-5 h-5" />
        <p className="font-medium">Couldn&apos;t load the tablet view</p>
      </div>
      <p className="text-sm text-muted-foreground">{error}</p>
      <Button variant="outline" size="sm" onClick={onRetry} data-testid="myroast-retry-btn">
        <RefreshCw className="w-4 h-4 mr-1.5" /> Try again
      </Button>
    </div>
  );
}

function NotConfiguredPanel() {
  return (
    <div
      className="flex flex-col items-start gap-4 rounded-lg border border-border bg-muted/30 p-6"
      data-testid="myroast-not-configured"
    >
      <div className="flex items-center gap-2">
        <Tablet className="w-5 h-5 text-muted-foreground" />
        <p className="font-medium">Tablet URL not configured yet</p>
      </div>
      <p className="text-sm text-muted-foreground max-w-2xl">
        The MyRoast tablet bundle is hosted on a separate domain. To wire
        it in, set <code className="px-1.5 py-0.5 rounded bg-background border">REACT_APP_MYROAST_URL</code>
        {" "}in <code className="px-1.5 py-0.5 rounded bg-background border">frontend/.env</code> to the
        hosted URL provided by the tablet team (e.g. <code className="px-1.5 py-0.5 rounded bg-background border">https://myroast.pages.dev</code>),
        then redeploy.
      </p>
      <p className="text-sm text-muted-foreground max-w-2xl">
        The backend side is already live —{" "}
        <code className="px-1.5 py-0.5 rounded bg-background border">/api/myroast/embed-token</code>{" "}
        is ready to mint short-lived JWTs for the iframe.
      </p>
    </div>
  );
}
